import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useASINSalesHistory } from '@/context/ASINSalesHistoryContext';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { SummaryCards } from '@/components/asin-sales-history/SummaryCards';
import { PerformanceCharts } from '@/components/asin-sales-history/PerformanceCharts';
import { QuickInsights } from '@/components/asin-sales-history/QuickInsights';
import { DateRangeSelector } from '@/components/asin-sales-history/DateRangeSelector';
import { LoadingIndicator } from '@/components/asin-sales-history/LoadingIndicator';
import { ASINMetrics, DateRangeValue } from '@/types/asinSalesHistory';
import { subDays, startOfDay } from 'date-fns';

// Helper function to normalize dates for consistent comparison
const normalizeDate = (date: Date): Date => {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
};

// Helper function to parse US date format (MM/DD/YYYY) consistently
const parseUSDate = (dateStr: string): Date | null => {
  try {
    // Handle various formats that might come from Google Sheets
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return normalizeDate(date);
  } catch {
    return null;
  }
};

const Dashboard: React.FC = () => {
  const {
    selectedCompany,
    companies,
    data: rawData,
    loading,
    error,
    selectedFilter,
    availableASINs,
    isLoadingAsins
  } = useASINSalesHistory();

  // Local state for date range and performance filters - no longer syncing with context
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    preset: 'last_30_days',
    startDate: subDays(new Date(), 30),
    endDate: new Date()
  });
  const [performanceFilter, setPerformanceFilter] = useState<'top_10' | 'bottom_10'>('top_10');
  const [filterType, setFilterType] = useState<'date' | 'asin'>('asin');
  const [initialLoad, setInitialLoad] = useState(false); // Start as false - no loading on first URL load

  // Track when initial load is complete - only set to true when we have a company selected and are loading data
  useEffect(() => {
    if (selectedCompany && loading) {
      setInitialLoad(true);
    } else if (rawData.length > 0 || error) {
      setInitialLoad(false);
    }
  }, [selectedCompany, loading, rawData.length, error]);

  // Check if we should show loading overlay (company selection loading)
  const shouldShowLoadingOverlay = loading || isLoadingAsins;

  // Memoized date range calculation to prevent flicker
  const dateRangeKey = useMemo(() => {
    return `${dateRange.preset}_${dateRange.startDate?.getTime()}_${dateRange.endDate?.getTime()}`;
  }, [dateRange]);

  // Single source of truth for filtered data - always start from raw data
  const filteredData = useMemo(() => {
    if (!rawData.length) return [];
    
    // First apply ASIN filter if selected (from context)
    let baseData = rawData;
    if (selectedFilter?.asin) {
      baseData = rawData.filter(row => row.asin === selectedFilter.asin);
    }
    
    // Then apply date filter locally (deterministic, based on date range only)
    if (!dateRange.startDate || !dateRange.endDate) return baseData;
    
    const startDate = normalizeDate(dateRange.startDate);
    const endDate = normalizeDate(dateRange.endDate);
    
    return baseData.filter(row => {
      const rowDate = parseUSDate(row.date.toString());
      if (!rowDate) return false;
      
      // Inclusive range check: [startDate, endDate]
      return rowDate >= startDate && rowDate <= endDate;
    });
  }, [rawData, selectedFilter?.asin, dateRangeKey]); // Use dateRangeKey for stable memoization

  // Calculate metrics from filtered data
  const metrics = useMemo((): ASINMetrics | null => {
    if (!filteredData.length) return null;

    const totalRevenue = filteredData.reduce((sum, row) => sum + row.revenue, 0);
    const totalUnits = filteredData.reduce((sum, row) => sum + row.units, 0);
    const totalGrossProfit = filteredData.reduce((sum, row) => sum + row.grossProfit, 0);
    const totalPPCSpend = filteredData.reduce((sum, row) => sum + row.ppcSpend, 0);
    const totalTraffic = filteredData.reduce((sum, row) => sum + row.traffic, 0);
    
    const avgConversionRate = filteredData.length > 0
      ? filteredData.reduce((sum, row) => sum + row.conversionRate, 0) / filteredData.length
      : 0;
    
    const avgTraffic = filteredData.length > 0
      ? totalTraffic / filteredData.length
      : 0;

    const profitMargin = totalRevenue > 0 ? (totalGrossProfit / totalRevenue) * 100 : 0;
    const acos = totalRevenue > 0 ? (totalPPCSpend / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalUnits,
      totalGrossProfit,
      totalPPCSpend,
      averageConversionRate: avgConversionRate,
      averageTraffic: avgTraffic,
      totalTraffic,
      profitMargin,
      acos
    };
  }, [filteredData]);

  // Calculate active ASINs count (memoized with stable dependencies)
  const activeASINs = useMemo(() => {
    const uniqueAsins = new Set(filteredData.filter(row => row.traffic > 0).map(row => row.asin));
    return uniqueAsins.size;
  }, [filteredData]);

  // Stable date range change handler to prevent unnecessary updates
  const handleDateRangeChange = useCallback((newDateRange: DateRangeValue) => {
    setDateRange(newDateRange);
  }, []);

  if (!selectedCompany) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-600" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No Company Selected
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                  Please select a company from the side panel to view ASIN sales history data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const company = companies.find(c => c.id === selectedCompany);

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-600" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                  Error Loading Data
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 max-w-md">
                  {error}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-auto">
      {/* Overlay loader for both initial and in-page loading */}
      {shouldShowLoadingOverlay && (
        <LoadingIndicator
          message={initialLoad ? "Loading ASIN Sales Data" : "Updating ASIN Data"}
          progress={initialLoad ? undefined : undefined} // Use auto-progress for all loading
          overlay={true}
          size="md"
        />
      )}
      
      {/* Main content with conditional dimming */}
      <div className={`p-4 md:p-6 space-y-4 md:space-y-6 max-w-full transition-opacity duration-200 ${shouldShowLoadingOverlay ? 'opacity-50' : 'opacity-100'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              Sales & Profit History
            </h1>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              {company?.client || selectedCompany}
              {selectedFilter?.asin && ` - ${selectedFilter.asin}`}
              {filteredData.length > 0 && ` (${filteredData.length} records)`}
            </p>
          </div>
          <div className="flex-shrink-0">
            <DateRangeSelector
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
        </div>

        <div className="space-y-6 md:space-y-8 w-full overflow-hidden">
          {/* Quick Insights - Moved to top */}
          <section className="w-full">
            <QuickInsights
              data={filteredData}
              metrics={metrics}
              loading={shouldShowLoadingOverlay}
            />
          </section>

          {/* ASIN Leaderboard Section */}
          <section className="w-full overflow-hidden">
            <PerformanceCharts
              data={filteredData}
              loading={shouldShowLoadingOverlay}
              performanceFilter={performanceFilter}
              filterType={filterType}
              selectedASIN={selectedFilter?.asin}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;